import React, { useEffect, useRef, useState, useCallback } from "react"
import { View, Text, TextStyle, StyleProp, ViewStyle } from "react-native"

type AutoSizeTextProps = {
  text: string
  maxFontSize?: number
  containerStyle?: StyleProp<ViewStyle>
  textStyle?: StyleProp<TextStyle>
}

const AutoShrinkText: React.FC<AutoSizeTextProps> = ({
  text,
  textStyle,
  containerStyle,
  maxFontSize
}: AutoSizeTextProps) => {
  const [fontSize, setFontSize] = useState<number>(maxFontSize ?? 16)
  const textRef = useRef<any>(null)
  const containerRef = useRef<any>(null)

  const measureTextWidth = useCallback(() => {
    if (!textRef.current || !containerRef.current) return
    const containerWidth = containerRef.current.getBoundingClientRect?.().width
    const textWidth = textRef.current.getBoundingClientRect?.().width
    if (!containerWidth || !textWidth) return

    // Shrink when overflowing
    if (textWidth > containerWidth + 1) {
      setFontSize(prevSize => Math.max(prevSize - 1, 10))
    } else if (maxFontSize && textWidth < containerWidth / 2) {
      // Gently expand back up toward max when there's ample space
      setFontSize(prevSize => Math.min((prevSize as number) + 1, maxFontSize))
    }
  }, [maxFontSize])

  useEffect(() => {
    measureTextWidth()
  }, [text, fontSize, measureTextWidth])

  useEffect(() => {
    const handleResize = () => {
      requestAnimationFrame(measureTextWidth)
    }
    window.addEventListener("resize", handleResize)
    return () => window.removeEventListener("resize", handleResize)
  }, [measureTextWidth])

  return (
    <View ref={containerRef} style={[containerStyle, { alignItems: "baseline", width: "100%", minWidth: 0 }]}>
      <View>
        <Text ref={textRef} style={[textStyle, { fontSize }]}>
          {text}
        </Text>
      </View>
    </View>
  )
}

export default AutoShrinkText

